// @ts-nocheck
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Typography from "@insite/mobius/Typography";
import { TypographyPresentationProps } from "@store/MobiusOverrides/Typography/Typography";
import { typographyTitleStyles } from "@store/Themes/common/mixins/typography";
import Carousel, { CarouselPresentationProps, CarouselSlide } from "@store/Overrides/Components/Carousel";
import constants from "@store/storeConstants";
import { pxToRemArray } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { spacingStyles } from "@toolkit/Themes/common/mixins/spacing";
import React, { FC, useEffect, useState } from "react";
import { css } from "styled-components";

const VIDEOS_CAROUSEL_ID = constants.carouselIds.videoCarousel;
export interface ProductDetailsVideosStyles {
    carousel?: CarouselPresentationProps;
    title?: TypographyPresentationProps;
}

type Props = WidgetProps & HasProduct;

export const productDetailsVideosStyles: ProductDetailsVideosStyles = {
    carousel: {
        extendedStyles: {
            carouselSlideInner: {
                css: css`
                    text-align: center;
                    > iframe {
                        margin: auto;
                        aspect-ratio: 16/9;
                        width: 100%;
                    }
                `,
            },
            carouselArrowPrevious: {
                direction: "left",
            },
            carouselArrowNext: {
                direction: "right",
            },
        },
    },
    title: {
        variant: null as any,
        forwardAs: "h2",
        css: css`
            ${typographyTitleStyles(pxToRemArray([20, null, 24]))}
            ${spacingStyles(null, "0.75em")}
        `,
    },
};

const styles = productDetailsVideosStyles;

const mapVideosToSlides = (videos: string[]) =>
    videos.map((video, index) => ({
        id: `video_${index}`,
        renderComponent: function VideoRenderer() {
            return (
                <iframe
                    src={`${video}`}
                    data-src={`${video}`}
                    title=""
                    frameBorder="0"
                    allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture;"
                    allowFullScreen
                ></iframe>
            );
        },
    }));

const validateVideoUrls = (videoUrls: string) => {
    const validUrls: string[] = [];
    const youTube = ["https://www.youtube.com/", "https://youtu.be/"];
    const vimeo = "https://vimeo.com/";
    const vimeoEmbed = "https://player.vimeo.com/video/";

    for (const videoObj of JSON.parse(videoUrls)) {
        const video = videoObj.filePath;

        if (video.startsWith(youTube[0])) {
            const embedUrl = video.includes(`${youTube}watch?v=`)
                ? `${youTube}embed/${video.replace(`${youTube[0]}watch?v=`, "")}`
                : video;
            validUrls.push(embedUrl);
        }
        if (video.startsWith(youTube[1])) {
            validUrls.push(video.replace(youTube[1], `${youTube[0]}embed/`));
        }
        if (video.startsWith(vimeo)) {
            validUrls.push(video.replace(vimeo, vimeoEmbed));
        }
    }

    return validUrls;
};

const ProductDetailsVideos: FC<Props> = ({ product }) => {
    const videoSource = product.properties.videos;
    const videos = videoSource ? validateVideoUrls(videoSource) : [];
    const [slides, setSlides] = useState<CarouselSlide[]>([]);

    useEffect(() => {
        if (videos.length) {
            setSlides(mapVideosToSlides(videos));
        }
    }, [JSON.stringify(videos)]);

    // Callback that resets videos in iframes so they don't keep playing as slides change.
    const slideCallback = () => {
        const carouselIframes = document.querySelectorAll(
            'div:not(.is-selected)>[data-test-selector="carousel_slide"]>iframe',
        );
        carouselIframes.forEach(function (item, index) {
            const iframeSrc = item.getAttribute("data-src");
            if (iframeSrc !== null) {
                item.setAttribute("src", "");
                item.setAttribute("src", iframeSrc);
            }
        });
    };

    if (slides.length === 0) {
        return null;
    }

    return (
        <>
            <Typography {...styles.title}>{translate("Video")}</Typography>
            <Carousel
                id={VIDEOS_CAROUSEL_ID}
                {...styles.carousel}
                maxNumberOfColumns={1}
                slides={slides}
                carouselCallBack={slideCallback}
                showDots
                speed={30}
            />
        </>
    );
};

const widgetModule: WidgetModule = {
    component: withProduct(ProductDetailsVideos),
    definition: {
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
        icon: "Carousel",
    },
};

export default widgetModule;
